import { useEffect, useLayoutEffect, useRef, useState } from 'react';

/**
 * Trunca texto em até `maxLines` linhas; expõe preview + flags para botões mais/menos.
 */
export default function useTruncatedText(
  text,
  { maxLines = 2, moreLabel = '', normalizeWhitespace = true, enabled = true } = {}
) {
  const hostRef = useRef(null);
  const measureRef = useRef(null);
  const [isExpanded, setIsExpanded] = useState(false);
  const [hasOverflow, setHasOverflow] = useState(false);
  const [preview, setPreview] = useState('');

  const resolvedText = (() => {
    if (text == null || text === '') {
      return '';
    }
    const raw = String(text);
    return normalizeWhitespace ? raw.replace(/\s+/g, ' ').trim() : raw.trim();
  })();

  useEffect(() => {
    setIsExpanded(false);
  }, [resolvedText, normalizeWhitespace, maxLines]);

  useLayoutEffect(() => {
    if (!enabled || !resolvedText) {
      setHasOverflow(false);
      setPreview('');
      return undefined;
    }

    const host = hostRef.current;
    const measure = measureRef.current;
    if (!host || !measure) {
      setHasOverflow(false);
      setPreview(resolvedText);
      return undefined;
    }

    const updatePreview = () => {
      const width = host.clientWidth;
      if (!width) {
        return;
      }

      measure.style.width = `${width}px`;
      const lineHeight =
        Number.parseFloat(window.getComputedStyle(measure).lineHeight) || 20;
      const maxHeight = lineHeight * maxLines + 1;
      const suffix = `... ${moreLabel}`;
      const fits = (candidate) => {
        measure.textContent = `${candidate}${suffix}`;
        return measure.scrollHeight <= maxHeight;
      };

      if (fits(resolvedText)) {
        setHasOverflow(false);
        setPreview(resolvedText);
        return;
      }

      let left = 0;
      let right = resolvedText.length;
      let best = 0;
      while (left <= right) {
        const mid = Math.floor((left + right) / 2);
        const candidate = resolvedText.slice(0, mid).trimEnd();
        if (candidate && fits(candidate)) {
          best = mid;
          left = mid + 1;
        } else {
          right = mid - 1;
        }
      }

      let nextPreview = resolvedText.slice(0, best).trimEnd();
      const lastSpace = nextPreview.lastIndexOf(' ');
      if (lastSpace > 0) {
        nextPreview = nextPreview.slice(0, lastSpace).trimEnd();
      }
      setHasOverflow(true);
      setPreview(
        nextPreview || resolvedText.slice(0, Math.max(best, 1)).trimEnd()
      );
    };

    updatePreview();

    if (typeof ResizeObserver !== 'undefined') {
      const resizeObserver = new ResizeObserver(updatePreview);
      resizeObserver.observe(host);
      return () => resizeObserver.disconnect();
    }

    if (typeof window !== 'undefined') {
      window.addEventListener('resize', updatePreview);
      return () => window.removeEventListener('resize', updatePreview);
    }

    return undefined;
  }, [enabled, resolvedText, moreLabel, maxLines]);

  return {
    hostRef,
    measureRef,
    isExpanded,
    setIsExpanded,
    hasOverflow,
    preview,
    fullText: resolvedText,
  };
}
